import React, {useEffect, useState} from "react";
import {DefaultContainer} from "@shared/DefaultContainer.tsx";
import {IRecursoMovimentacao} from "@models/recurso/IRecursoMovimentacao.ts";
import {GetRecursoMovimentacoes} from "@localization/siteMap/endpoints/_RecursosEndpoints.ts";
import {IObjectCounter, IPagination} from "@localization/siteMap/siteMapInterfaces.ts";
import {Button, Grid, ToggleButton, ToggleButtonGroup} from "@mui/material";
import {BarChart} from '@mui/x-charts/BarChart';
import {CreaterecursoMovimentacaoComponent} from "@components/recurso/CreateRecursoMovimentacaoComponent.tsx";
import {AddOutlined, SearchOutlined} from "@mui/icons-material";
import {useAlert} from "@contexts/hooks/useAlert.ts";
import {DatePicker} from "@mui/x-date-pickers";
import dayjs from "dayjs";
import {RecursoMovimentacaoInformacao} from "@components/recurso/movimentacao/RecursoMovimentacaoInformacao.tsx";
import {useGeneral} from "@contexts/GeneralContext";
import {RecursoMovimentacaoFilterDto} from "@models/recurso/RecursoMovimentacaoDto";
import {useApi} from "@contexts/hooks/api/useApi.ts";

export interface RecursoMovimentacoesPageProps extends RecursoMovimentacaoFilterDto {
    onCreate?: (movimentacao: IRecursoMovimentacao) => void;
}

type visualizacao = 'Tabela' | 'Grafico'

export const RecursoMovimentacoesPage: React.FC<RecursoMovimentacoesPageProps> = (props) => {
    const {Get} = useApi();
    const {addPopUp, closePopUp} = useAlert();
    const {screenSizeLevel} = useGeneral();

    const [visualizacao, setVisualizacao] = useState<visualizacao>('Tabela');
    const [filters, setFilters] = useState<RecursoMovimentacaoFilterDto>({
        ...props,
        dataInicial: dayjs(props.dataInicial ?? '2000-01-01'),
        dataFinal: dayjs(props.dataFinal ?? '2100-01-01'),
    });

    const [movimentacoes, setMovimentacoes] = useState<IRecursoMovimentacao[]>();
    const [counters, setCounters] = useState<IObjectCounter[]>([]);
    const [pagination, setPagination] = useState<IPagination>({
            page: 0, pageSize: 10,
        },
    );

    useEffect(() => {
        if (pagination.page == 0) {
            setPagination({...pagination, page: 1});
            return;
        }
        setPagination(prev => ({...prev, loading: true}));
        Get(GetRecursoMovimentacoes({...filters}, pagination)).then(x => {
            if (x.status == 'Success') {
                setMovimentacoes(x.items);
                setPagination(prev => ({...prev, ...x.pagination}));
                if (x.counters)
                    setCounters(x.counters as IObjectCounter[]);
            }
        }).finally(() => {
            setPagination(prev => ({...prev, loading: false, ignoreCache: false}))
        });
    }, [pagination.page, pagination.ignoreCache == true]);

    return (<DefaultContainer pageProps={{pagination, setPagination}}
                              permissaoNecessaria={['VisualizarRecursoInformacoes']}>
        <Grid container justifyContent={'space-between'}>
            <Grid>
                <ToggleButtonGroup size={'small'}>
                    <ToggleButton value={'Tabela'} onClick={() => setVisualizacao('Tabela')}
                                  selected={visualizacao == 'Tabela'}>Tabela</ToggleButton>
                    <ToggleButton value={'Grafico'} onClick={() => setVisualizacao('Grafico')}
                                  selected={visualizacao == 'Grafico'}>Grafico</ToggleButton>
                </ToggleButtonGroup>
            </Grid>
            <Grid>
                <Button variant={'outlined'} endIcon={<AddOutlined/>}
                        onClick={() => {
                            addPopUp({
                                title: 'Criar movimentação',
                                content: <CreaterecursoMovimentacaoComponent recursoId={props.recursoId}
                                                                             onCreate={(v) => {
                                                                                 setPagination({
                                                                                     ...pagination,
                                                                                     page: 0
                                                                                 });
                                                                                 closePopUp();
                                                                                 if (props.onCreate)
                                                                                     props.onCreate(v);
                                                                             }}/>
                            });
                        }}
                >Criar movimentacao</Button>
            </Grid>
        </Grid>
        <Grid container justifyContent={'space-between'} sx={{mb: 1, mt: 1}}>
            <Grid container>
                <Grid size={screenSizeLevel > 6 ? 12 : 6}>
                    <DatePicker
                        label={'Data inicial'}
                        value={filters.dataInicial}
                        maxDate={filters.dataFinal}
                        minDate={dayjs('2000-01-01')}
                        onChange={(newValue) => {
                            setFilters({
                                ...filters,
                                dataInicial: dayjs(newValue),
                            });
                        }}
                        localeText={{
                            'todayButtonLabel': 'Hoje',
                        }}
                        slotProps={{
                            textField: {size: 'small', variant: 'filled', fullWidth: true}, actionBar: {
                                actions: ['today'],
                            },
                        }}
                    />
                </Grid>
                <Grid size={screenSizeLevel > 6 ? 12 : 6}>
                    <DatePicker

                        label={'Data final'}
                        value={filters.dataFinal}
                        minDate={filters.dataInicial}
                        maxDate={dayjs().add(5000, 'year')}
                        localeText={{
                            'todayButtonLabel': 'Hoje',
                        }}
                        slotProps={{
                            textField: {size: 'small', variant: 'filled', fullWidth: true}, actionBar: {
                                actions: ['today'],
                            },
                        }}
                        onChange={(newValue) => {
                            setFilters({
                                ...filters,
                                dataFinal: dayjs(newValue),
                            });
                        }}
                    />
                </Grid>
            </Grid>
            <Grid>
                <Button fullWidth
                        loading={pagination.loading ?? false}
                        variant={'outlined'}
                        sx={{maxHeight: '100%', height: '100%'}} onClick={() => {
                    setPagination({...pagination, page: 0});
                }}
                        endIcon={<SearchOutlined/>}
                >Filtrar</Button>
            </Grid>
        </Grid>


        {visualizacao == 'Tabela' && <Grid>
            {movimentacoes?.map(x => {
                return <RecursoMovimentacaoInformacao value={x} variant={"list"}/>;
            })}
        </Grid>

        }
        {visualizacao == 'Grafico' && <Grid container>
            <Grid size={12}>
                <BarChart height={300}
                          xAxis={[{data: counters.map(x => x.name)}]}
                          series={[{data: counters.map(x => Math.abs(x.value ?? 0))}]}

                />
            </Grid>
        </Grid>
        }
    </DefaultContainer>);
};